/**
 * «Начать с нуля» · окно 6 запуска — тексты и правила ОПАСНОЙ ЗОНЫ.
 *
 * Десятая часть витрины с настоящей логикой, и ошибка здесь дороже обычной: экран,
 * который неверно понял подтверждение или соврал про «что останется», отнимает у
 * человека месяцы работы. Поэтому правила вынесены сюда и проверяются запуском:
 *
 *   cd Lang/web && node tools/check-reset.mjs
 *
 * ⭐ ЗАЩИТА ОТ СЛУЧАЙНОГО НАЖАТИЯ — ТРЕМЯ СЛОЯМИ (требование владельца):
 *   1. раздел живёт В САМОЙ ГЛУБИНЕ настроек, отдельным экраном — не рядом с темой;
 *   2. подтверждение ВВОДОМ СЛОВА, а не вторым тапом: «Вы уверены?» прожимают не читая;
 *   3. снимок и видимая кнопка «вернуть как было» столько дней, сколько сказало ядро.
 *
 * ⛔ И ни одного собственного решения: фраза подтверждения, срок отката, список кнопок
 *   и их описания приезжают ОТ ЯДРА (`core/reset.py`). Витрина сверяет введённое слово
 *   тем же правилом, что ядро, — но последнее слово всё равно за ядром: оно ответит 400.
 */

/** ⭐ Запасная фраза на случай, если экран рисуется до ответа сервера. Правда — в ядре. */
export const CONFIRM_FALLBACK = "НАЧАТЬ С НУЛЯ";

export type ScopeKey = "srs" | "awards" | "xp";
export const SCOPE_ORDER: ScopeKey[] = ["srs", "awards", "xp"];

export const DANGER_TITLE = "Опасная зона";
export const DANGER_HINT =
  "Здесь можно начать заново. Каждая кнопка сбрасывает своё — и только у тебя: " +
  "чужой прогресс это не трогает.";

/** ⛔ Что не исчезает НИКОГДА. Стоит на экране крупно: без этого сброс выглядит как «снесёт всё». */
export const KEEPS_ALWAYS =
  "Словарь, переводы, примеры и озвучка остаются целиком — их сброс не трогает никогда. " +
  "Занятия из истории тоже никуда не деваются: обнуляется то, что из них считается.";

export const CONFIRM_LABEL = (phrase: string) => `Впиши «${phrase}», чтобы подтвердить`;
export const CONFIRM_WRONG = "Слово не совпало — сброса не будет.";
export const BUTTON_LABEL = "Сбросить";
export const UNDO_LABEL = "Вернуть как было";

/* ── падежи ────────────────────────────────────────────────────────────────── */

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function daysWord(n: number): string {
  return `${n} ${plural(n, "день", "дня", "дней")}`;
}

export function wordsWord(n: number): string {
  return `${n} ${plural(n, "слово", "слова", "слов")}`;
}

export function awardsWord(n: number): string {
  return `${n} ${plural(n, "награда", "награды", "наград")}`;
}

export function daysStreakWord(n: number): string {
  return `серия ${daysWord(n)}`;
}

/* ── подтверждение ─────────────────────────────────────────────────────────── */

/** Ввод человека → сравнимая строка. Регистр и лишние пробелы прощаем (клавиатура). */
export function normalize(text: unknown): string {
  return String(text ?? "").split(/\s+/).filter(Boolean).join(" ").toUpperCase();
}

/**
 * Совпало ли подтверждение. ⛔ Пустая строка — НЕ подтверждение (иначе «Сбросить»
 * срабатывало бы с пустым полем, то есть ровно тем самым случайным нажатием).
 * ⭐ Правило то же, что в ядре (`reset.confirm_ok`), и это сверяется запуском.
 */
export function confirmOk(text: unknown, phrase = CONFIRM_FALLBACK): boolean {
  const typed = normalize(text);
  return typed.length > 0 && typed === normalize(phrase);
}

/** Можно ли нажимать кнопку сброса прямо сейчас. */
export function canReset(text: unknown, phrase = CONFIRM_FALLBACK, busy = false): boolean {
  return !busy && confirmOk(text, phrase);
}

/* ── что исчезнет и что останется ──────────────────────────────────────────── */

export interface Lost {
  srs: { words_touched: number; words_total: number; known: number };
  awards: { earned: number };
  xp: { xp_total: number; level: number; streak_days: number };
  kept: { words: number; answers: number };
}

/**
 * Одна строка «что именно ты потеряешь» под каждой кнопкой — цифрами из ядра.
 * ⭐ Когда терять нечего, так и пишем: кнопка, обещающая сброс пустоты, врёт.
 */
export function lossLine(scope: ScopeKey, lost: Lost | null): string {
  if (!lost) return "";
  if (scope === "srs") {
    const n = lost.srs.words_touched;
    if (!n) return "Пока нечего сбрасывать: ни одно слово ещё не сдвинулось с места.";
    return `Сейчас со ступенями памяти ${wordsWord(n)}` +
      (lost.srs.known ? `, из них выучено ${lost.srs.known}` : "") + ".";
  }
  if (scope === "awards") {
    const n = lost.awards.earned;
    return n ? `Сейчас получено ${awardsWord(n)}.` : "Пока не получено ни одной награды.";
  }
  const xp = lost.xp;
  if (!xp.xp_total && !xp.streak_days) return "Опыта пока нет — сбрасывать нечего.";
  return `Сейчас ${xp.xp_total} опыта, уровень ${xp.level}, ` +
    `${daysStreakWord(xp.streak_days)}.`;
}

/** Что останется — цифрами, а не обещанием. Показывается один раз, над кнопками. */
export function keptLine(lost: Lost | null): string {
  if (!lost) return KEEPS_ALWAYS;
  return `${KEEPS_ALWAYS} У тебя это ${wordsWord(lost.kept.words)} и ` +
    `${lost.kept.answers} ${plural(lost.kept.answers, "ответ", "ответа", "ответов")} ` +
    "в истории занятий.";
}

/* ── снимок и откат ────────────────────────────────────────────────────────── */

export interface Snapshot {
  id: number;
  scope: string;
  title: string;
  rows: number;
  created_at: string;
  expires_at: string;
  restored_at: string | null;
  can_undo: boolean;
}

/** Снимки, которыми ещё можно вернуть. ⛔ Использованный и протухший не показываем. */
export function undoable(snaps: Snapshot[] | null | undefined): Snapshot[] {
  return (snaps ?? []).filter((s) => s.can_undo);
}

/** «2026-08-18 21:40:00» → «18.08 в 21:40». Дата без формата читается как машинный лог. */
export function whenLabel(stamp: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(stamp ?? ""));
  if (!m) return String(stamp ?? "");
  return `${m[3]}.${m[2]} в ${m[4]}:${m[5]}`;
}

/**
 * Подпись под кнопкой отката. ⭐ Честно предупреждает о цене: «как было» значит
 * именно как было — работа после сброса пропадёт.
 */
export function undoLine(snap: Snapshot): string {
  return `«${snap.title}» сброшены ${whenLabel(snap.created_at)}. ` +
    `Вернуть можно до ${whenLabel(snap.expires_at)} — состояние станет ровно таким, ` +
    "каким было до сброса, а занятия после сброса в счёт не пойдут.";
}

/** Обещание срока отката на экране сброса. Число — от ядра, не своё. */
export function undoPromise(days: number): string {
  return `Передумаешь — ${daysWord(days)} будет кнопка «${UNDO_LABEL}».`;
}

/** Что сказать после успешного сброса. */
export function doneLine(title: string, days: number): string {
  return `Готово: «${title}». ${undoPromise(days)}`;
}
